import React, { useContext, useEffect, useState } from 'react';
import { Trophy, Medal, Award, CheckCircle, Navigation, Users, Flame, Star } from 'lucide-react';
import { UserContext } from '../App';
import { supabase } from '../utils/supabaseClient';
import { LEVELS } from '../utils/levels';
import MemberCenterLayout from '../components/common/MemberCenterLayout';

const BADGE_DEFINITIONS = [
  {
    id: 'welcome',
    name: '初來乍到',
    description: '完成註冊並填寫個人資料',
    icon: <CheckCircle size={24} />,
    color: '#10b981',
    bgColor: '#d1fae5',
    check: (profile) => profile?.name && profile?.birthday
  },
  {
    id: 'organizer',
    name: '揪團發起人',
    description: '曾發起過揪旅伴或揪活動',
    icon: <Navigation size={24} />,
    color: '#3b82f6',
    bgColor: '#dbeafe',
    check: async (userId) => {
      try {
        const { count: travelCount } = await supabase.from('travel_posts').select('*', { count: 'exact', head: true }).eq('organizer_id', userId);
        const { count: eventCount } = await supabase.from('events').select('*', { count: 'exact', head: true }).eq('organizer_id', userId);
        return (travelCount > 0) || (eventCount > 0);
      } catch (err) { return false; }
    }
  },
  {
    id: 'social',
    name: '社交達人',
    description: '成功結交好友',
    icon: <Users size={24} />,
    color: '#8b5cf6',
    bgColor: '#ede9fe',
    check: async (userId) => {
      try {
        const { count } = await supabase.from('friendships').select('*', { count: 'exact', head: true }).or(`user_id.eq.${userId},friend_id.eq.${userId}`).eq('status', 'accepted');
        return count > 0;
      } catch (err) { return false; }
    }
  },
  {
    id: 'active',
    name: '熱血活躍',
    description: '累積積分超過300分',
    icon: <Flame size={24} />,
    color: '#ef4444',
    bgColor: '#fee2e2',
    check: (profile) => (profile?.total_points || 0) >= 300
  },
  {
    id: 'founding',
    name: '創始會員',
    description: '早期加入的專屬認證',
    icon: <Star size={24} />,
    color: '#f59e0b',
    bgColor: '#fef3c7',
    check: (profile) => profile?.badge === 'founding'
  }
];

export default function LevelAndBadges() {
  const { currentUser } = useContext(UserContext);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [isLoadingBadges, setIsLoadingBadges] = useState(true);

  useEffect(() => {
    if (!currentUser) return;
    const computeBadges = async () => {
      try {
        const earned = [];
        for (const badge of BADGE_DEFINITIONS) {
          let hasBadge = false;
          if (badge.id === 'organizer' || badge.id === 'social') {
            hasBadge = await badge.check(currentUser.id);
          } else {
            hasBadge = badge.check(currentUser.profile);
          }
          if (hasBadge) earned.push(badge.id);
        }
        setEarnedBadges(earned);
      } catch (err) {
        console.error('Failed to compute badges:', err);
      } finally {
        setIsLoadingBadges(false);
      }
    };
    computeBadges();
  }, [currentUser]);

  return (
    <MemberCenterLayout title="成就與勳章" subtitle="查看積分等級與已解鎖的專屬徽章" activeKey="badges">
        <div style={{ maxWidth: '820px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Badges Section */}
          <div style={{ background: 'white', padding: '24px', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Award size={20} color="#f59e0b" />
              我的勳章庫
            </h2>

            {isLoadingBadges ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>讀取勳章紀錄中...</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '20px' }}>
                {BADGE_DEFINITIONS.map((badge) => {
                  const isEarned = earnedBadges.includes(badge.id);
                  return (
                    <div key={badge.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', opacity: isEarned ? 1 : 0.4, transition: 'all 0.3s', filter: isEarned ? 'none' : 'grayscale(100%)' }}>
                      <div style={{
                        width: '72px', height: '72px', borderRadius: '50%',
                        background: badge.bgColor, color: badge.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: isEarned ? `0 6px 16px ${badge.color}40` : 'none',
                        border: `2px solid ${isEarned ? badge.color : '#e5e7eb'}`
                      }}>
                        {badge.icon}
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#374151', whiteSpace: 'nowrap', marginBottom: '4px' }}>{badge.name}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.2 }}>{badge.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Points Rules Section */}
          <div style={{ background: 'white', padding: '24px', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)', marginBottom: '40px' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Trophy size={20} color="#4e8bca" />
              積分與等級說明
            </h2>

            <div style={{ marginBottom: '32px' }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '15px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Medal size={16} color="#4e8bca" />
                等級門檻
              </h4>
              <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '12px' }}>
                {LEVELS.map((lv, i) => (
                  <div key={lv.level} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 4px', borderBottom: i < LEVELS.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                    <span style={{ fontWeight: '600', color: '#334155', fontSize: '15px' }}>LV.{lv.level} {lv.title}</span>
                    <span style={{ color: '#64748b', fontSize: '15px', fontWeight: '500' }}>{lv.minPoints}分</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 style={{ margin: '0 0 12px', fontSize: '15px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Trophy size={16} color="#f59e0b" />
                如何獲得積分？
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { action: '發起揪旅伴或揪活動', pts: '+10分' },
                  { action: '報名參加活動', pts: '+5分' },
                  { action: '結交一位新好友', pts: '+2分' },
                  { action: '在活動留言互動', pts: '+1分' },
                  { action: '完善個人資料', pts: '+15分' }
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px' }}>
                    <span style={{ fontSize: '15px', color: '#4b5563', fontWeight: '500' }}>{item.action}</span>
                    <span style={{ fontWeight: '700', color: '#10b981', fontSize: '15px' }}>{item.pts}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
    </MemberCenterLayout>
  );
}
